import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { api as apiClient } from '../services/api';
import toast from '../utils/toast';
import { getDocumentRequestType } from '../config/documentRequests';

const invalidateDocumentRequests = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['documentRequests'] });
  queryClient.invalidateQueries({ queryKey: ['timeline', 'my-hr-letters'] });
};

const invalidateDocumentTemplates = (queryClient) => {
  queryClient.invalidateQueries({
    queryKey: ['documentRequests', 'admin', 'templates'],
  });
  queryClient.invalidateQueries({
    queryKey: ['documentRequests', 'templates'],
  });
};

const collectPaginatedResults = async (initialRequest) => {
  let response = await initialRequest;
  const results = [];
  let pageCount = 0;

  while (response) {
    const data = response.data;
    if (Array.isArray(data)) {
      results.push(...data);
      break;
    }
    results.push(...(data?.results || []));
    if (!data?.next) break;

    pageCount += 1;
    if (pageCount >= 100) {
      throw new Error('The document queue exceeded the safe pagination limit.');
    }
    response = await apiClient.get(data.next);
  }

  return results;
};

export const readDocumentRequestError = async (
  error,
  fallback = 'The request could not be completed.',
) => {
  const data = error?.response?.data;
  if (data instanceof Blob || (data && typeof data.text === 'function')) {
    try {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text || '{}');
        return (
          parsed.error
          || parsed.detail
          || parsed.letter_generation_warning
          || fallback
        );
      } catch {
        return text?.trim() || fallback;
      }
    } catch {
      return fallback;
    }
  }
  if (typeof data === 'string') return data;
  if (Array.isArray(data)) return data.join(' ');
  if (data && typeof data === 'object') {
    const direct = data.error || data.detail || data.letter_generation_warning;
    if (typeof direct === 'string') return direct;
    const first = Object.values(data)[0];
    if (typeof first === 'string') return first;
    if (Array.isArray(first)) return first.join(' ');
  }
  return error?.message || fallback;
};

const showMutationError = (error, fallback) => {
  readDocumentRequestError(error, fallback).then((message) => {
    toast.error(message);
  });
};

export const usePublishedDocumentTemplates = (requestType) =>
  useQuery({
    queryKey: ['documentRequests', 'templates', requestType],
    queryFn: async () => {
      const type = getDocumentRequestType(requestType);
      const response = await apiClient.get(
        '/configuration/document-templates/published/',
        { params: { document_type: type.documentTypeName } },
      );
      return response.data?.results || response.data || [];
    },
    enabled: !!requestType,
    staleTime: 10 * 60 * 1000,
  });

export const useMyDocumentRequests = () =>
  useQuery({
    queryKey: ['documentRequests', 'my'],
    queryFn: async () => {
      const response = await api.getMyDocumentRequests();
      return response.data?.results || response.data || [];
    },
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
  });

export const useDocumentRequestQueue = () =>
  useQuery({
    queryKey: ['documentRequests', 'queue'],
    queryFn: async () => {
      const [submitted, approved, letters] = await Promise.all([
        collectPaginatedResults(
          api.getDocumentRequests({
            status: 'submitted',
            ordering: '-created_at',
          }),
        ),
        collectPaginatedResults(
          api.getDocumentRequests({
            status: 'approved',
            ordering: '-created_at',
          }),
        ),
        collectPaginatedResults(
          api.getHRLetters({ ordering: '-generated_at' }),
        ),
      ]);
      return {
        requests: [...submitted, ...approved],
        letters,
      };
    },
    staleTime: 30 * 1000,
  });

export const useDocumentTemplates = () =>
  useQuery({
    queryKey: ['documentRequests', 'admin', 'templates'],
    queryFn: async () => {
      const response = await api.getDocumentTemplates({
        ordering: '-updated_at',
        page_size: 500,
      });
      return response.data?.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

export const useDocumentTypes = () =>
  useQuery({
    queryKey: ['documentRequests', 'admin', 'documentTypes'],
    queryFn: async () => {
      const response = await api.getDocumentTypes({ page_size: 500 });
      return response.data?.results || response.data || [];
    },
    staleTime: 10 * 60 * 1000,
  });

export const useDocumentTemplateMergeFields = (enabled = true) =>
  useQuery({
    queryKey: ['documentRequests', 'admin', 'mergeFields'],
    queryFn: async () => {
      const response = await apiClient.get(
        '/configuration/document-templates/merge_fields/',
      );
      return response.data || [];
    },
    enabled,
    staleTime: 30 * 60 * 1000,
  });

export const useDocumentRequestPreview = (requestId, enabled = true) =>
  useQuery({
    queryKey: ['documentRequests', 'preview', requestId],
    queryFn: async () => {
      const response = await api.previewDocumentRequestPdf(requestId);
      return response.data;
    },
    enabled: enabled && !!requestId,
    staleTime: 30 * 1000,
    gcTime: 60 * 1000,
    retry: false,
  });

export const useHRLetterPreview = (letterId, enabled = true) =>
  useQuery({
    queryKey: ['documentRequests', 'letterPreview', letterId],
    queryFn: async () => {
      const response = await api.previewHRLetter(letterId);
      return response.data;
    },
    enabled: enabled && !!letterId,
    staleTime: 30 * 1000,
    gcTime: 60 * 1000,
    retry: false,
  });

export const useDocumentTemplatePreview = (templateId, enabled = true) =>
  useQuery({
    queryKey: ['documentRequests', 'templatePreview', templateId],
    queryFn: async () => {
      const response = await apiClient.get(
        `/configuration/document-templates/${templateId}/preview_pdf/`,
        { responseType: 'blob' },
      );
      return response.data;
    },
    enabled: enabled && !!templateId,
    staleTime: 60 * 1000,
    gcTime: 2 * 60 * 1000,
    retry: false,
  });

export const useCreateDocumentRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.createDocumentRequest(data),
    onSuccess: () => {
      invalidateDocumentRequests(queryClient);
      toast.success('Letter request submitted to HR');
    },
    onError: (error) => {
      showMutationError(error, 'Could not submit the letter request.');
    },
  });
};

export const useCancelDocumentRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }) =>
      api.cancelDocumentRequest(id, { reason }),
    onSuccess: () => {
      invalidateDocumentRequests(queryClient);
      toast.success('Letter request cancelled');
    },
    onError: (error) => {
      showMutationError(error, 'Could not cancel the letter request.');
    },
  });
};

export const useApproveDocumentRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.approveDocumentRequest(id),
    onSuccess: (response) => {
      invalidateDocumentRequests(queryClient);
      if (response.data?.letter_generation_warning) {
        toast.warning(response.data.letter_generation_warning);
        return;
      }
      toast.success('Request approved and letter generated');
    },
    onError: (error) => {
      showMutationError(
        error,
        'Approval could not be completed. The request remains pending.',
      );
    },
  });
};

export const useRejectDocumentRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }) => api.rejectDocumentRequest(id, reason),
    onSuccess: () => {
      invalidateDocumentRequests(queryClient);
      toast.success('Request rejected and employee notified');
    },
    onError: (error) => {
      showMutationError(error, 'Could not reject the letter request.');
    },
  });
};

export const useRetryDocumentRequestGeneration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.retryDocumentRequestGeneration(id),
    onSuccess: () => {
      invalidateDocumentRequests(queryClient);
      toast.success('Letter generated successfully');
    },
    onError: (error) => {
      showMutationError(
        error,
        'The letter could not be generated. The request remains available for retry.',
      );
    },
  });
};

export const useRegenerateHRLetter = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.regenerateHRLetter(id),
    onSuccess: () => {
      invalidateDocumentRequests(queryClient);
      toast.success('Letter PDF restored');
    },
    onError: (error) => {
      showMutationError(error, 'Could not restore the letter PDF.');
    },
  });
};

export const useSaveDocumentTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) =>
      id
        ? api.updateDocumentTemplate(id, data)
        : api.createDocumentTemplate(data),
    onSuccess: (_response, variables) => {
      invalidateDocumentTemplates(queryClient);
      toast.success(variables.id ? 'Template updated' : 'Template created as draft');
    },
    onError: (error) => {
      showMutationError(error, 'Could not save the document template.');
    },
  });
};

export const usePublishDocumentTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.publishDocumentTemplate(id),
    onSuccess: () => {
      invalidateDocumentTemplates(queryClient);
      toast.success('Template published for employee requests');
    },
    onError: (error) => {
      showMutationError(error, 'Could not publish the document template.');
    },
  });
};

export const useArchiveDocumentTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.archiveDocumentTemplate(id),
    onSuccess: () => {
      invalidateDocumentTemplates(queryClient);
      toast.success('Template archived');
    },
    onError: (error) => {
      showMutationError(error, 'Could not archive the document template.');
    },
  });
};

export const useDeleteDocumentTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.deleteDocumentTemplate(id),
    onSuccess: () => {
      invalidateDocumentTemplates(queryClient);
      toast.success('Draft template deleted');
    },
    onError: (error) => {
      showMutationError(error, 'Could not delete the draft template.');
    },
  });
};
